import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { ApiResponse, MbrsStatusEnum } from '../../../types/commons';
import { RuntimeConfigService } from '../common/runtime-config.service';

/**
 * AS-842 — Frontend write-back of a validated conversion status.
 *
 * Single responsibility (SRP): POST the derived terminal status
 * (`Success` | `SuccessWithWarnings` | `Review`) to the backend, after
 * `ConversionStatusResolverService` has resolved a row currently in
 * `PendingValidation`. The backend's allow-list (see
 * `MbrsService.UpdateConversionStatus`) enforces these three values.
 *
 * The TypeScript signature on `postStatus` is intentionally narrow so
 * accidental callers cannot POST `Failed`, `InProgress`, `AwaitingFiles`,
 * or `PendingValidation` — those are backend-owned.
 *
 * Errors are not swallowed here; the caller (resolver) decides whether
 * to keep the resolved value displayed locally and retry on next listing
 * reload, or surface the failure to the user.
 */
@Injectable({ providedIn: 'root' })
export class ConversionStatusWriteService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);

  postStatus(
    conversionId: string,
    status:
      | MbrsStatusEnum.Success
      | MbrsStatusEnum.SuccessWithWarnings
      | MbrsStatusEnum.Review
  ): Observable<void> {
    const url = `${this.config.apiUrl()}/v2/mbrs/update-conversion-status`;
    const body = { conversionId, status };

    return this.http
      .post<ApiResponse<void>>(url, body, { withCredentials: true })
      .pipe(
        map(() => void 0),
        catchError((error: HttpErrorResponse) =>
          throwError(() => error)
        )
      );
  }
}
